// Suppressions.js - Manage finding suppressions for monitored assets
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    listSuppressions,
    createSuppression,
    updateSuppression,
    removeSuppression,
    getSuppressionAudit
} from '../services/suppressionService';
import { getAssets } from '../services/assetService';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import './Suppressions.css';

function Suppressions() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [suppressions, setSuppressions] = useState([]);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // PRD-49: bulkBatchId filter from URL
    const bulkBatchId = searchParams.get('bulkBatchId') || '';

    // Filters
    const [assetFilter, setAssetFilter] = useState(searchParams.get('assetId') || '');
    const [statusFilter, setStatusFilter] = useState(bulkBatchId ? 'all' : 'active');
    const [typeFilter, setTypeFilter] = useState('');
    const [dispositionFilter, setDispositionFilter] = useState('');

    // Audit modal
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditData, setAuditData] = useState(null);
    const [loadingAudit, setLoadingAudit] = useState(false);

    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editFields, setEditFields] = useState({ expiresAt: '', reason: '', disposition: '' });

    // Create modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newSuppression, setNewSuppression] = useState({
        assetId: '',
        findingType: 'vulnerability',
        findingIdentifier: '',
        findingName: '',
        reason: '',
        disposition: 'accept',
        scope: 'host'
    });

    // Load suppressions
    const loadSuppressions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const filters = { status: statusFilter };
            if (assetFilter) filters.assetId = assetFilter;
            if (typeFilter) filters.findingType = typeFilter;
            if (dispositionFilter) filters.disposition = dispositionFilter;
            if (bulkBatchId) filters.bulkBatchId = bulkBatchId;

            const data = await listSuppressions(filters);
            setSuppressions(data.suppressions || []);
        } catch (err) {
            console.error('Failed to load suppressions:', err);
            setError(err.message || 'Failed to load suppressions');
        } finally {
            setLoading(false);
        }
    }, [assetFilter, statusFilter, typeFilter, dispositionFilter, bulkBatchId]);

    // Load assets for filter dropdown
    const loadAssets = useCallback(async () => {
        try {
            const data = await getAssets();
            setAssets(data.assets || []);
        } catch (err) {
            console.error('Failed to load assets:', err);
        }
    }, []);

    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    useEffect(() => {
        loadSuppressions();
    }, [loadSuppressions]);

    // Handle remove suppression
    const handleRemove = async (suppression) => {
        const confirmMsg = `Remove suppression for "${suppression.findingName}"?\n\nThis finding will now appear in reports and affect risk scoring.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            await removeSuppression(suppression.suppressionId);
            setSuccessMessage(`Suppression removed for ${suppression.findingName}`);
            setTimeout(() => setSuccessMessage(null), 3000);
            loadSuppressions();
        } catch (err) {
            setError(err.message || 'Failed to remove suppression');
        }
    };

    // Handle view audit trail
    const handleViewAudit = async (suppression) => {
        try {
            setLoadingAudit(true);
            setShowAuditModal(true);
            const data = await getSuppressionAudit(suppression.suppressionId);
            setAuditData(data);
        } catch (err) {
            setError(err.message || 'Failed to load audit trail');
            setShowAuditModal(false);
        } finally {
            setLoadingAudit(false);
        }
    };

    // Handle create suppression
    const handleCreate = async (e) => {
        e.preventDefault();

        // Validate
        if (!newSuppression.assetId) {
            setError('Please select an asset');
            return;
        }
        if (!newSuppression.findingIdentifier.trim()) {
            setError('Please enter a finding identifier (e.g., CVE-2024-1234 or port number)');
            return;
        }
        if (!newSuppression.findingName.trim()) {
            setError('Please enter a finding name');
            return;
        }
        if (!newSuppression.reason.trim() || newSuppression.reason.trim().length < 10) {
            setError('Please enter a reason (minimum 10 characters)');
            return;
        }

        try {
            setCreateLoading(true);
            setError(null);

            await createSuppression({
                assetId: newSuppression.assetId,
                findingType: newSuppression.findingType,
                findingIdentifier: newSuppression.findingIdentifier.trim(),
                findingName: newSuppression.findingName.trim(),
                reason: newSuppression.reason.trim(),
                disposition: newSuppression.disposition,
                scope: newSuppression.scope
            });

            setSuccessMessage('Suppression created successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
            setShowCreateModal(false);
            setNewSuppression({
                assetId: '',
                findingType: 'vulnerability',
                findingIdentifier: '',
                findingName: '',
                reason: '',
                disposition: 'accept',
                scope: 'host'
            });
            loadSuppressions();
        } catch (err) {
            setError(err.message || 'Failed to create suppression');
        } finally {
            setCreateLoading(false);
        }
    };

    // Open create modal
    const openCreateModal = () => {
        setNewSuppression({
            assetId: assetFilter || '',
            findingType: 'vulnerability',
            findingIdentifier: '',
            findingName: '',
            reason: '',
            disposition: 'accept',
            scope: 'host'
        });
        setShowCreateModal(true);
    };

    // Navigate to asset
    const handleViewAsset = (assetId) => {
        navigate(`/assets/${assetId}`);
    };

    // Get asset value by ID
    const getAssetValue = (assetId) => {
        const asset = assets.find(a => a.assetId === assetId);
        return asset ? asset.value : assetId;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get type badge style
    const getTypeBadgeClass = (type) => {
        switch (type) {
            case 'vulnerability': return 'type-vuln';
            case 'port': return 'type-port';
            case 'service': return 'type-service';
            default: return '';
        }
    };

    // Get disposition badge
    const getDispositionBadge = (disposition) => {
        if (disposition === 'accept') return <span className="disposition-badge disposition-accept">Accept</span>;
        if (disposition === 'ignore') return <span className="disposition-badge disposition-ignore">Ignore</span>;
        return <span className="disposition-badge">-</span>;
    };

    // Get severity badge
    const getSeverityBadge = (severity) => {
        if (!severity) return null;
        return <span className={`severity-badge severity-${severity}`}>{severity}</span>;
    };

    // Format relative expiration
    const formatExpiry = (expiresAt) => {
        if (!expiresAt) return 'Never';
        const expires = new Date(expiresAt);
        const now = new Date();
        const diffMs = expires - now;
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return <span className="expiry-past">Expired</span>;
        if (diffDays === 0) return <span className="expiry-soon">Today</span>;
        if (diffDays === 1) return <span className="expiry-soon">Tomorrow</span>;
        if (diffDays <= 30) return <span className="expiry-soon">in {diffDays} days</span>;
        return <span title={expires.toISOString()}>in {diffDays} days</span>;
    };

    // Handle edit suppression
    const handleEdit = (suppression) => {
        setEditTarget(suppression);
        setEditFields({
            expiresAt: suppression.expiresAt ? suppression.expiresAt.split('T')[0] : '',
            reason: suppression.reason || '',
            disposition: suppression.disposition || 'accept'
        });
        setShowEditModal(true);
    };

    // Handle save edit
    const handleSaveEdit = async () => {
        if (!editTarget) return;
        try {
            setEditLoading(true);
            setError(null);
            const updates = {};
            if (editFields.expiresAt) {
                updates.expiresAt = new Date(editFields.expiresAt + 'T00:00:00Z').toISOString();
            }
            if (editFields.reason && editFields.reason !== editTarget.reason) {
                updates.reason = editFields.reason;
            }
            if (editFields.disposition && editFields.disposition !== editTarget.disposition) {
                updates.disposition = editFields.disposition;
            }
            await updateSuppression(editTarget.suppressionId, updates);
            setSuccessMessage('Suppression updated successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
            setShowEditModal(false);
            loadSuppressions();
        } catch (err) {
            setError(err.message || 'Failed to update suppression');
        } finally {
            setEditLoading(false);
        }
    };

    return (
        <CleanModernLayout activePage="suppressions">
        <div className="suppressions-page">
            {/* Page Header */}
            <header className="suppressions-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <span className="terminal-prompt">SUPPRESSIONS</span>
                    </h1>
                    <p className="page-subtitle">
                        Manage suppressed findings and ports across your monitored assets
                    </p>
                </div>
            </header>

            {/* PRD-49: Bulk batch filter banner */}
            {bulkBatchId && (
                <div className="success-banner" style={{ marginBottom: 12 }}>
                    <span className="success-icon">B</span>
                    <span className="success-message">
                        Showing suppressions from bulk batch <strong>{bulkBatchId}</strong>.{' '}
                        <a href="/suppressions" style={{ color: 'inherit', textDecoration: 'underline' }}>
                            Clear filter
                        </a>
                    </span>
                </div>
            )}

            {/* Error Banner */}
            {error && (
                <div className="error-banner">
                    <span className="error-icon">!</span>
                    <span className="error-message">{error}</span>
                    <button className="error-dismiss" onClick={() => setError(null)}>x</button>
                </div>
            )}

            {/* Success Banner */}
            {successMessage && (
                <div className="success-banner">
                    <span className="success-icon">ok</span>
                    <span className="success-message">{successMessage}</span>
                </div>
            )}

            {/* Filters */}
            <div className="filters-bar">
                <div className="filter-group">
                    <label>Asset:</label>
                    <select
                        value={assetFilter}
                        onChange={(e) => setAssetFilter(e.target.value)}
                    >
                        <option value="">All Assets</option>
                        {assets.map(asset => (
                            <option key={asset.assetId} value={asset.assetId}>
                                {asset.value}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Status:</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="active">Active</option>
                        <option value="removed">Removed</option>
                        <option value="expired">Expired</option>
                        <option value="all">All</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Type:</label>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="vulnerability">Vulnerabilities</option>
                        <option value="port">Ports</option>
                        <option value="service">Services</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Disposition:</label>
                    <select
                        value={dispositionFilter}
                        onChange={(e) => setDispositionFilter(e.target.value)}
                    >
                        <option value="">All</option>
                        <option value="accept">Accept</option>
                        <option value="ignore">Ignore</option>
                    </select>
                </div>

                <button className="btn-refresh" onClick={loadSuppressions}>
                    Refresh
                </button>

                <button className="btn-create" onClick={openCreateModal}>
                    + New Suppression
                </button>
            </div>

            {/* Suppressions Table */}
            <div className="suppressions-table-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading suppressions...</p>
                    </div>
                ) : suppressions.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">[  ]</div>
                        <h3>No Suppressions Found</h3>
                        <p>
                            {statusFilter === 'active'
                                ? 'You have no active suppressions. Suppress findings from the Asset Details page.'
                                : 'No suppressions match your current filters.'}
                        </p>
                    </div>
                ) : (
                    <table className="suppressions-table">
                        <thead>
                            <tr>
                                <th>Asset</th>
                                <th>Finding</th>
                                <th>Type</th>
                                <th>Disposition</th>
                                <th>Scope</th>
                                <th>Severity</th>
                                <th>Expires</th>
                                <th>Reason</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {suppressions.map(suppression => (
                                <tr key={suppression.suppressionId} className={suppression.status === 'removed' ? 'row-removed' : ''}>
                                    <td>
                                        <button
                                            className="link-btn"
                                            onClick={() => handleViewAsset(suppression.assetId)}
                                        >
                                            {getAssetValue(suppression.assetId)}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="finding-info">
                                            <span className="finding-name">{suppression.findingName}</span>
                                            <span className="finding-id">{suppression.findingIdentifier}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`type-badge ${getTypeBadgeClass(suppression.findingType)}`}>
                                            {suppression.findingType}
                                        </span>
                                    </td>
                                    <td>{getDispositionBadge(suppression.disposition)}</td>
                                    <td>
                                        <span className="scope-badge">
                                            {suppression.matchedAt ? suppression.matchedAt : 'Asset-wide'}
                                        </span>
                                    </td>
                                    <td>{getSeverityBadge(suppression.severity)}</td>
                                    <td title={suppression.expiresAt}>{formatExpiry(suppression.expiresAt)}</td>
                                    <td>
                                        <span className="reason-text" title={suppression.reason}>
                                            {suppression.reason.length > 50
                                                ? `${suppression.reason.substring(0, 50)}...`
                                                : suppression.reason}
                                        </span>
                                    </td>
                                    <td>{formatDate(suppression.createdAt)}</td>
                                    <td>
                                        <span className={`status-badge status-${suppression.status}`}>
                                            {suppression.status}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn-action btn-audit"
                                            onClick={() => handleViewAudit(suppression)}
                                            title="View audit trail"
                                        >
                                            History
                                        </button>
                                        {suppression.status === 'active' && (
                                            <button
                                                className="btn-action btn-edit"
                                                onClick={() => handleEdit(suppression)}
                                                title="Edit expiration"
                                            >
                                                Edit
                                            </button>
                                        )}
                                        {suppression.status === 'active' && (
                                            <button
                                                className="btn-action btn-remove"
                                                onClick={() => handleRemove(suppression)}
                                                title="Remove suppression"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Audit Modal */}
            {showAuditModal && (
                <div className="modal-overlay" onClick={() => setShowAuditModal(false)}>
                    <div className="modal-content audit-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Audit Trail</h2>
                            <button className="modal-close" onClick={() => setShowAuditModal(false)}>x</button>
                        </div>

                        {loadingAudit ? (
                            <div className="modal-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading audit trail...</p>
                            </div>
                        ) : auditData ? (
                            <div className="modal-body">
                                <div className="suppression-summary">
                                    <h3>{auditData.suppression?.findingName}</h3>
                                    <p className="suppression-id">{auditData.suppression?.findingIdentifier}</p>
                                </div>

                                <div className="audit-timeline">
                                    {auditData.auditTrail?.map((entry, index) => (
                                        <div key={entry.auditId || index} className={`audit-entry action-${entry.action}`}>
                                            <div className="audit-marker"></div>
                                            <div className="audit-content">
                                                <div className="audit-action">
                                                    {entry.action === 'created' && 'Suppression Created'}
                                                    {entry.action === 'removed' && 'Suppression Removed'}
                                                    {entry.action === 'modified' && 'Suppression Modified'}
                                                    {entry.action === 'expired' && 'Suppression Expired'}
                                                    {entry.action === 'modified-disposition' && 'Disposition Changed'}
                                                    {entry.action === 'modified-expiry' && 'Expiration Modified'}
                                                    {entry.action === 'modified-expiry-expired' && 'Expired (manually)'}
                                                    {entry.action === 'reopened-by-expiry' && 'Alarm Re-opened'}
                                                </div>
                                                <div className="audit-meta">
                                                    <span className="audit-user">{entry.userEmail}</span>
                                                    <span className="audit-time">{formatDate(entry.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="modal-empty">
                                <p>No audit data available</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Create Suppression Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content create-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create Suppression</h2>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>x</button>
                        </div>

                        <form onSubmit={handleCreate} className="create-form">
                            <div className="form-field">
                                <label>Asset *</label>
                                <select
                                    value={newSuppression.assetId}
                                    onChange={(e) => setNewSuppression({ ...newSuppression, assetId: e.target.value })}
                                    disabled={createLoading}
                                    required
                                >
                                    <option value="">Select an asset...</option>
                                    {assets.map(asset => (
                                        <option key={asset.assetId} value={asset.assetId}>
                                            {asset.value}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-field">
                                <label>Finding Type *</label>
                                <select
                                    value={newSuppression.findingType}
                                    onChange={(e) => setNewSuppression({ ...newSuppression, findingType: e.target.value })}
                                    disabled={createLoading}
                                >
                                    <option value="vulnerability">Vulnerability</option>
                                    <option value="port">Port</option>
                                    <option value="service">Service</option>
                                </select>
                            </div>

                            <div className="form-field">
                                <label>
                                    {newSuppression.findingType === 'vulnerability' && 'CVE or Template ID *'}
                                    {newSuppression.findingType === 'port' && 'Port Number *'}
                                    {newSuppression.findingType === 'service' && 'Service Name *'}
                                </label>
                                <input
                                    type="text"
                                    value={newSuppression.findingIdentifier}
                                    onChange={(e) => setNewSuppression({ ...newSuppression, findingIdentifier: e.target.value })}
                                    placeholder={
                                        newSuppression.findingType === 'vulnerability' ? 'e.g., CVE-2024-1234 or ssl-heartbleed' :
                                        newSuppression.findingType === 'port' ? 'e.g., 22 or 8080' :
                                        'e.g., SSH or MySQL'
                                    }
                                    disabled={createLoading}
                                    required
                                />
                            </div>

                            <div className="form-field">
                                <label>Finding Name *</label>
                                <input
                                    type="text"
                                    value={newSuppression.findingName}
                                    onChange={(e) => setNewSuppression({ ...newSuppression, findingName: e.target.value })}
                                    placeholder="Human-readable name for this finding"
                                    disabled={createLoading}
                                    required
                                />
                            </div>

                            <div className="form-field">
                                <label>Reason * (min 10 characters)</label>
                                <textarea
                                    value={newSuppression.reason}
                                    onChange={(e) => setNewSuppression({ ...newSuppression, reason: e.target.value })}
                                    placeholder="Why is this finding being suppressed? Include ticket reference if applicable."
                                    rows={3}
                                    disabled={createLoading}
                                    required
                                    minLength={10}
                                />
                                <span className="char-count">{newSuppression.reason.length} / 10 min</span>
                            </div>

                            <div className="form-field">
                                <label>Disposition *</label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="disposition"
                                            value="accept"
                                            checked={newSuppression.disposition === 'accept'}
                                            onChange={(e) => setNewSuppression({ ...newSuppression, disposition: e.target.value })}
                                            disabled={createLoading}
                                        />
                                        <span className="radio-text">Accept</span>
                                        <span className="radio-hint">Real risk, acknowledged. Still counts in risk score.</span>
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="disposition"
                                            value="ignore"
                                            checked={newSuppression.disposition === 'ignore'}
                                            onChange={(e) => setNewSuppression({ ...newSuppression, disposition: e.target.value })}
                                            disabled={createLoading}
                                        />
                                        <span className="radio-text">Ignore</span>
                                        <span className="radio-hint">False positive. Removed from risk score.</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-field">
                                <label>Scope</label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="scope"
                                            value="host"
                                            checked={newSuppression.scope === 'host'}
                                            onChange={(e) => setNewSuppression({ ...newSuppression, scope: e.target.value })}
                                            disabled={createLoading}
                                        />
                                        <span className="radio-text">This host only</span>
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="scope"
                                            value="asset"
                                            checked={newSuppression.scope === 'asset'}
                                            onChange={(e) => setNewSuppression({ ...newSuppression, scope: e.target.value })}
                                            disabled={createLoading}
                                        />
                                        <span className="radio-text">Entire asset</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={createLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-submit"
                                    disabled={createLoading}
                                >
                                    {createLoading ? 'Creating...' : 'Create Suppression'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Edit Suppression Modal */}
            {showEditModal && editTarget && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Edit Suppression</h2>
                            <button className="modal-close" onClick={() => setShowEditModal(false)}>x</button>
                        </div>

                        <div className="modal-body">
                            <div className="suppression-summary">
                                <h3>{editTarget.findingName}</h3>
                                <p className="suppression-id">{editTarget.findingIdentifier}</p>
                            </div>

                            <div className="form-field">
                                <label>Disposition</label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="edit-disposition"
                                            value="accept"
                                            checked={editFields.disposition === 'accept'}
                                            onChange={(e) => setEditFields({ ...editFields, disposition: e.target.value })}
                                            disabled={editLoading}
                                        />
                                        <span className="radio-text">Accept</span>
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="edit-disposition"
                                            value="ignore"
                                            checked={editFields.disposition === 'ignore'}
                                            onChange={(e) => setEditFields({ ...editFields, disposition: e.target.value })}
                                            disabled={editLoading}
                                        />
                                        <span className="radio-text">Ignore</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-field">
                                <label>Expiration Date</label>
                                <input
                                    type="date"
                                    value={editFields.expiresAt}
                                    onChange={(e) => setEditFields({ ...editFields, expiresAt: e.target.value })}
                                    disabled={editLoading}
                                />
                            </div>

                            <div className="form-field">
                                <label>Reason</label>
                                <textarea
                                    value={editFields.reason}
                                    onChange={(e) => setEditFields({ ...editFields, reason: e.target.value })}
                                    rows={3}
                                    disabled={editLoading}
                                    minLength={10}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn-cancel"
                                onClick={() => setShowEditModal(false)}
                                disabled={editLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleSaveEdit}
                                disabled={editLoading}
                            >
                                {editLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </CleanModernLayout>
    );
}

export default Suppressions;
